import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Logo from './Logo';
import TestInterface from './TestInterface';
import TestResultsDisplay from './TestResultsDisplay';
import { GuestErrorBoundary } from './UserTypeErrorBoundaries';
// import { FullScreenLoading, DataLoading, LoadingOverlay } from './LoadingStates';
import { NetworkAwareRetry } from './RetryMechanism';
import ErrorDisplay, { NetworkStatus } from './ErrorDisplay';
import { useErrorHandling, useNetworkAwareOperation } from '../hooks/useErrorHandling';
import { useGuestSession } from '../hooks/useGuestSession';
import type { TestResults, TestType } from '../types';
import { ROUTES } from '../config/routes';
import { useAuth } from '../contexts/AuthContext';

interface GuestInfo {
  name: string;
  age?: number;
  department?: string;
}

type FlowStep = 'info' | 'test' | 'results';

interface GuestTestFlowProps {
  initialStep?: FlowStep;
  sessionId?: string;
}

/**
 * Complete guest test flow component that handles:
 * 1. Minimal information collection
 * 2. Test execution identical to authenticated users
 * 3. Immediate printable results
 * 4. Session management (UI state only)
 */
const GuestTestFlow: React.FC<GuestTestFlowProps> = ({ 
  initialStep,
  sessionId: propSessionId 
}) => {
  const navigate = useNavigate();
  const { sessionId: paramSessionId } = useParams();
  const location = useLocation();
  const { 
    createGuestSession, 
    saveGuestTestResults, 
    guestSession, 
    guestUser,
    isSessionValid,
    terminateGuestSession 
  } = useGuestSession();
  const { isAuthenticated, logout } = useAuth();

  const [currentStep, setCurrentStep] = useState<FlowStep>('info');
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    name: '',
    age: undefined,
    department: undefined
  });
  const [errors, setErrors] = useState<Partial<Record<keyof GuestInfo, string>>>({});
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [isSavingGuestResults, setIsSavingGuestResults] = useState(false);
  const [resultSaveWarning, setResultSaveWarning] = useState<string | null>(null);
  const [testConfig, setTestConfig] = useState<{ testDuration: number; testType: TestType; isMockup: boolean }>({
    testDuration: 600000, // 10 minutes in milliseconds
    testType: 'CHECKERBOARD',
    isMockup: false,
  });

  // Enhanced error handling
  const {
    error,
    isLoading,
    handleError,
    clearError,
    canRetry,
  } = useErrorHandling({
    maxRetries: 3,
    retryDelay: 2000,
    onError: (error) => {
      console.error('Guest test flow error:', error);
    },
  });

  // Network awareness
  const { isOnline, executeOrQueue } = useNetworkAwareOperation();

  // Determine active session ID
  const activeSessionId = propSessionId || paramSessionId || guestSession?.sessionId;

  // Initialize component based on URL and session state
  useEffect(() => {
    // console.log('🔄 GuestTestFlow useEffect - initialStep:', initialStep, 'activeSessionId:', activeSessionId, 'guestSession:', guestSession);
    
    // If initialStep is explicitly provided (e.g., from GuestResults page), respect it
    if (initialStep) {
      // Check if we have results data in navigation state for results step
      if (initialStep === 'results') {
        console.log('🔍 Results step - checking navigation state');
        const navigationState = location.state as any;
        console.log('Navigation state:', navigationState);
        
        if (navigationState?.testResults && navigationState?.guestInfo) {
          console.log('Found results in navigation state, restoring...');
          // Restore session and results from navigation state ONLY if not already set
          if (!guestSession || guestSession.sessionId !== navigationState.guestInfo.sessionId) {
            createGuestSession(navigationState.guestInfo);
          }
          setTestResults(navigationState.testResults);
          setResultSaveWarning(navigationState.resultSaveWarning || null);
          setGuestInfo(navigationState.guestInfo);
          setTestConfig(navigationState.testConfig || testConfig);
          setCurrentStep('results');
          return;
        }
        
        
        // Try to recover from session if navigation state is not available
        if (guestSession && guestSession.testResults) {
          setTestResults(guestSession.testResults);
          setGuestInfo(guestSession.guestInfo);
          setCurrentStep('results');
          return;
        }
        
        // No results found, redirect to info
        setCurrentStep('info');
        return;
      }
      
      // Setting step from initialStep
      setCurrentStep(initialStep);
      
      // If explicitly starting with info step, clear any existing session to start fresh
      if (initialStep === 'info' && guestSession) {
        terminateGuestSession();
      }
      return;
    }

    // If we have a sessionId in the URL (from navigation), we should be in test mode
    if (activeSessionId && !initialStep) {
      // Session ID found in URL, checking session state
      
      if (guestSession && isSessionValid()) {
        if (guestSession.testResults) {
          setCurrentStep('results');
          setTestResults(guestSession.testResults);
        } else {
          setCurrentStep('test');
          setGuestInfo(guestSession.guestInfo);
        }
      } else {
        // Check if session data was passed through navigation state
        const navigationState = location.state as any;
        
        if (navigationState?.guestSession && navigationState.guestSession.sessionId === activeSessionId) {
          // Restore the session in the AuthContext
          createGuestSession(navigationState.guestInfo);
          setCurrentStep('test');
          setGuestInfo(navigationState.guestInfo);
          setTestConfig(navigationState.testConfig || testConfig);
          return;
        }
        
        // Try to recover session from sessionStorage as fallback
        const storedSession = sessionStorage.getItem('guestSession');
        if (storedSession) {
          try {
            const session = JSON.parse(storedSession);
            if (session.sessionId === activeSessionId) {
              // Restore the session in the AuthContext
              createGuestSession(session.guestInfo);
              setCurrentStep('test');
              setGuestInfo(session.guestInfo);
              return;
            }
          } catch (error) {
            console.error('Failed to parse stored session:', error);
          }
        }
        
        // Could not recover session, redirect to info
        setCurrentStep('info');
      }
      return;
    }

    // If we have an active session and it's valid, determine step based on session state
    if (guestSession && isSessionValid()) {
      if (guestSession.testResults) {
        console.log('📊 Session has results, going to results step');
        setCurrentStep('results');
        setTestResults(guestSession.testResults);
      } else {
        console.log('🎯 Session exists, going to test step');
        setCurrentStep('test');
        setGuestInfo(guestSession.guestInfo);
      }
    } else {
      console.log('📝 No valid session, going to info step');
      setCurrentStep('info');
    }
  }, [initialStep, activeSessionId]); //guestSession, isSessionValid, , location.state

  useEffect(() => {
    if (isAuthenticated) {
      logout();
    }
  }, [isAuthenticated, logout]);

  const validateGuestInfo = (): boolean => {
    const newErrors: Partial<Record<keyof GuestInfo, string>> = {};
    
    if (!guestInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (guestInfo.age && (guestInfo.age < 16 || guestInfo.age > 100)) {
      newErrors.age = 'Age must be between 16 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartTest = async (e?: React.FormEvent) => {
    // Prevent form submission if this is called from a form
    if (e) {
      e.preventDefault();
    }

    if (!validateGuestInfo()) {
      return;
    }

    try {
      // Store test config in sessionStorage for the test interface
      sessionStorage.setItem('guestTestConfig', JSON.stringify(testConfig));
      
      // Create or update guest session
      const session = createGuestSession(guestInfo);
      
      // Generate the route
      const testRoute = ROUTES.GUEST.TEST(session.sessionId);
      
      // Navigate to test step with session ID, passing session data in state
      navigate(testRoute, { 
        replace: true,
        state: { 
          guestSession: session,
          guestInfo: guestInfo,
          testConfig: testConfig
        }
      });
      // Navigation completed
    } catch (error) {
      console.error('❌ Error in handleStartTest:', error);
      handleError(error, 'Failed to start test');
    }
  };

  const handleTestComplete = async (results: TestResults) => {
    // Clean up stored test config since test is complete
    sessionStorage.removeItem('guestTestConfig');
    const sessionIdForResults = activeSessionId || guestSession?.sessionId || 'session';
    let finalResults = results;
    let saveWarning: string | null = null;
    setIsSavingGuestResults(true);
    setResultSaveWarning(null);

    // Persist online and display server-computed metrics when available.
    if (activeSessionId) {
      try {
        const { dataPersistenceService } = await import('../services/dataPersistenceService');
        const response = await executeOrQueue(
          () => dataPersistenceService.submitGuestTestResults(
            activeSessionId,
            {
              reactionTimes: results.rawReactionTimes,
              lapses: results.totalLapses,
              falseStarts: results.totalFalseStarts,
              totalResponses: results.totalResponses,
              averageReactionTime: results.averageReactionTime,
              testDuration: results.testDuration ?? testConfig.testDuration / 1000,
              guestInfo: {
                name: guestInfo.name,
                age: guestInfo.age,
                company: guestInfo.department,
              },
              companyContext: {
                source: 'web-app',
                department: guestInfo.department || 'Unknown',
              },
              testType: testConfig.testType,
            },
            { retryOnFailure: true, maxRetries: 3, validateData: true, includeMetadata: true }
          ),
          { executeOffline: false, queueWhenOffline: false }
        );

        if (response?.success && response.data) {
          const saved = response.data.results;
          finalResults = {
            ...results,
            averageReactionTime: saved.averageReactionTime,
            medianReactionTime: saved.medianReactionTime,
            totalLapses: saved.totalLapses,
            lapseCount: saved.totalLapses,
            majorLapses: saved.majorLapses,
            minorLapses: saved.minorLapses,
            totalFalseStarts: saved.totalFalseStarts,
            accuracy: saved.accuracy,
            completedAt: new Date(saved.completedAt),
          };
        } else {
          saveWarning = response?.error?.message || 'Results could not be saved. They remain available in this browser session.';
        }
      } catch (error) {
        console.error('Failed to save guest test results:', error);
        saveWarning = 'Results could not be saved. They remain available in this browser session.';
      }
    }

    setResultSaveWarning(saveWarning);
    saveGuestTestResults(finalResults);
    setTestResults(finalResults);
    setIsSavingGuestResults(false);

    // Navigate to results step with results data (always)
    navigate(ROUTES.GUEST.RESULTS(sessionIdForResults), { 
      replace: true,
      state: {
        guestSession: guestSession,
        guestInfo: guestInfo,
        testResults: finalResults,
        testConfig: testConfig,
        resultSaveWarning: saveWarning,
      }
    });
    setCurrentStep('results');
  };

  const handleStartNewTest = () => {
    // Clear current session and start fresh
    terminateGuestSession();
    setTestResults(null);
    setResultSaveWarning(null);
    setGuestInfo({ name: '', age: undefined, department: undefined });
    setTestConfig({
      testDuration: 600000, // Reset to default 10 minutes
      testType: 'CHECKERBOARD',
      isMockup: false,
    });
    
    // Clear stored test config
    sessionStorage.removeItem('guestTestConfig');
    
    setCurrentStep('info');
    navigate(ROUTES.GUEST.INFO, { replace: true });
  };

  const handleReturnToHome = () => {
    terminateGuestSession();
    navigate(ROUTES.HOME);
  };

  const renderInfoStep = () => (
    <GuestErrorBoundary sessionId={activeSessionId}>
      <NetworkAwareRetry
        fallbackMessage="Guest test requires an internet connection for result saving."
        showQueueStatus={true}
      >
        <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
          {/* Network status indicator */}
          <NetworkStatus isOnline={isOnline} className="fixed top-4 left-4 right-4 z-20" />

          <div className="min-h-screen flex items-center justify-center p-6">
            <Card className="w-full max-w-lg bg-navy-800 shadow-xl rounded-2xl border border-navy-700">
              <CardHeader className="text-center pb-6">
                 <div className="flex justify-center mb-4">
                   <Logo size="lg" />
                 </div>
                 <h1 className="text-2xl font-bold text-white">Vigikus Fatigue Check</h1>
                 <p className="mt-2 text-navy-200">Provide a few details to begin.</p>
              </CardHeader>

              <CardContent className="space-y-6">
                <form onSubmit={handleStartTest} noValidate autoComplete="off">
              {/* Error display */}
              {error && (
                <ErrorDisplay
                  error={error}
                  onRetry={canRetry ? () => clearError() : undefined}
                  onDismiss={clearError}
                  variant="inline"
                />
              )}
              {/* Name Field */}
              <div>
                <Label htmlFor="name" className="block text-sm font-medium text-navy-100 mb-2">
                  Full Name *
                </Label>
                <Input
                  type="text"
                  id="name"
                  value={guestInfo.name}
                  onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                  className={errors.name ? 'border-red-500' : ''}
                  placeholder="Enter your full name"
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                )}
              </div>

              {/* Age Field (Optional) */}
              <div>
                <Label htmlFor="age" className="block text-sm font-medium text-navy-100 mb-2">
                  Age (Optional)
                </Label>
                <Input
                  type="number"
                  id="age"
                  value={guestInfo.age || ''}
                  onChange={(e) => setGuestInfo(prev => ({ 
                    ...prev, 
                    age: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  className={errors.age ? 'border-red-500' : ''}
                  placeholder="Enter your age"
                  min="16"
                  max="100"
                />
                {errors.age && (
                  <p className="mt-1 text-sm text-red-400">{errors.age}</p>
                )}
              </div>

              {/* Department/Company Field (Optional) */}
              <div>
                <Label htmlFor="department" className="block text-sm font-medium text-navy-100 mb-2">
                  Department/Company (Optional)
                </Label>
                <Input
                  type="text"
                  id="department"
                  value={guestInfo.department || ''}
                  onChange={(e) => setGuestInfo(prev => ({ ...prev, department: e.target.value }))}
                  placeholder="Enter your department or company"
                />
              </div>

              {/* Test Duration Selection */}
              <div>
                <Label htmlFor="testDuration" className="block text-sm font-medium text-navy-100 mb-2">
                  Test Duration
                </Label>
                <Select
                  value={testConfig.testDuration.toString()}
                  onValueChange={(value) => setTestConfig(prev => ({ 
                    ...prev, 
                    testDuration: parseInt(value) 
                  }))}
                >
                  <SelectTrigger id="testDuration" className="w-full">
                    <SelectValue placeholder="Select test duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60000">1 Minute (Quick Test)</SelectItem>
                    <SelectItem value="180000">3 Minutes (Short)</SelectItem>
                    <SelectItem value="300000">5 Minutes (Standard)</SelectItem>
                    <SelectItem value="600000">10 Minutes (Full Assessment)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-sm text-navy-300">
                  Actual test duration. The optional mockup before it always runs for 30 seconds.
                </p>
              </div>

              {/* Test Type Selection */}
              <div>
                <Label htmlFor="testType" className="block text-sm font-medium text-navy-100 mb-2">
                  Test Type
                </Label>
                <Select
                  value={testConfig.testType}
                  onValueChange={(value) => setTestConfig(prev => ({
                    ...prev,
                    testType: value as TestType
                  }))}
                >
                  <SelectTrigger id="testType" className="w-full">
                    <SelectValue placeholder="Select test type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECKERBOARD">Checkerboard Pattern (Standard)</SelectItem>
                    <SelectItem value="LETTER">Letter Recall (Memory)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-sm text-navy-300">
                  {testConfig.testType === 'LETTER'
                    ? 'Memorize 3 letters, then respond when one appears among 5 random letters'
                    : 'Respond as quickly as possible when the checkerboard pattern appears'}
                </p>
              </div>

              {/* Mockup Option */}
              <label className="flex min-h-11 items-start gap-3 text-sm text-navy-100">
                <input
                  type="checkbox"
                  checked={testConfig.isMockup}
                  onChange={(e) => setTestConfig(prev => ({ ...prev, isMockup: e.target.checked }))}
                  className="mt-0.5 text-gold-400 focus:ring-gold-500"
                />
                <span>
                  <span className="font-medium">Mockup Test (30 seconds)</span>
                  <span className="block text-xs text-navy-300">
                    Runs a 30-second practice before the actual test. Mockup results are not saved.
                  </span>
                </span>
              </label>

              {/* Privacy Notice */}
              <div className="bg-navy-900/60 border border-navy-700 rounded-lg p-4">
                <p className="text-sm text-gold-300">
                  <strong>Data notice:</strong> Your test results and the details you provide are saved by Vigikus to support this assessment and service analytics.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  onClick={handleReturnToHome}
                  variant="outline"
                  className="flex-1 py-3"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !isOnline}
                  className="flex-1 bg-gold-500 hover:bg-gold-600 py-3"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-navy-950" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </div>
                  ) : !isOnline ? (
                    'No Internet Connection'
                  ) : (
                    'Start Test'
                  )}
                </Button>
              </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </NetworkAwareRetry>
    </GuestErrorBoundary>
  );

  const renderTestStep = () => {
    if (isSavingGuestResults) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-navy-950 p-6" role="status">
          <p className="text-navy-100">Saving your Fatigue Check results…</p>
        </div>
      );
    }

    if (!guestUser || !activeSessionId) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-navy-200 mb-4">Session not found</p>
            <Button onClick={handleStartNewTest}>Start New Test</Button>
          </div>
        </div>
      );
    }

    return (
      <TestInterface
        testId={activeSessionId}
        userType="guest"
        testConfig={{
          testDuration: testConfig.testDuration,
          sessionId: activeSessionId,
          testType: testConfig.testType,
          isMockup: testConfig.isMockup
        }}
        onTestComplete={(results) => {
          // Convert TestInterface results to TestResults format
          const testResults: TestResults = {
            id: `guest-${activeSessionId}-${Date.now()}`,
            sessionId: activeSessionId,
            userId: undefined, // Guest users don't have user IDs
            highestReactionTime: Math.max(...results.reactionTimes),
            lowestReactionTime: Math.min(...results.reactionTimes),
            averageReactionTime: results.averageReactionTime,
            medianReactionTime: results.medianReactionTime,
            totalResponses: results.totalResponses,
            totalLapses: results.lapses,
            majorLapses: results.majorLapses,
            minorLapses: results.minorLapses,
            missedTargets: results.missedTargets,
            totalFalseStarts: results.falseStarts,
            fastestTenMean: results.fastestTenMean,
            slowestTenMean: results.slowestTenMean,
            reactionTimeStdDev: results.reactionTimeStdDev,
            alertnessLevel: results.alertnessLevel,
            rawReactionTimes: results.reactionTimes,
            lapseCount: results.lapses,
            accuracy: results.totalResponses > 0 
              ? ((results.totalResponses - results.falseStarts) / results.totalResponses) * 100 
              : 0,
            testDuration: results.testDuration,
            completedAt: new Date(),
            userType: 'guest'
          };
          handleTestComplete(testResults);
        }}
      />
    );
  };

  const renderResultsStep = () => {
    console.log('🔍 renderResultsStep - testResults:', testResults, 'guestUser:', guestUser);
    
    if (!testResults || !guestUser) {
      console.log('❌ Missing data for results - testResults:', !!testResults, 'guestUser:', !!guestUser);
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-navy-200 mb-4">No test results found</p>
            <Button onClick={handleStartNewTest}>Start New Test</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        {/* Header with navigation options (screen only) */}
        <div className="print:hidden p-6">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline" 
                onClick={handleStartNewTest}
                className="border-navy-600 text-gold-400 hover:bg-navy-800"
              >
                Take Another Test
              </Button>
              <Button 
                variant="outline" 
                onClick={handleReturnToHome}
                className="border-navy-700 text-navy-100 hover:bg-navy-900"
              >
                Return to Home
              </Button>
            </div>
          </div>
        </div>

        {/* Results content */}
        <div className="p-6">
          <div className="max-w-5xl mx-auto">
            {resultSaveWarning && (
              <div role="alert" className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                {resultSaveWarning}
              </div>
            )}
            <TestResultsDisplay
              test={{
                id: testResults.testId || testResults.sessionId || guestUser.sessionId,
                sessionId: guestUser.sessionId,
                testDate: testResults.completedAt instanceof Date
                  ? testResults.completedAt
                  : new Date(testResults.completedAt),
                status: 'completed',
                userType: 'guest',
                testType: testConfig.testType,
                results: testResults
              }}
              showUserInfo={false}
              showTestInfo={true}
            />
          </div>
        </div>

      </div>
    );
  };

  // Render appropriate step
  switch (currentStep) {
    case 'info':
      return renderInfoStep();
    case 'test':
      return renderTestStep();
    case 'results':
      return renderResultsStep();
    default:
      return renderInfoStep();
  }
};

export default GuestTestFlow;
